
import React, { useState } from "react";
import { ChevronDown, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useWorkout } from "@/contexts/WorkoutContext";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

// Energy levels with more engaging names and descriptions
const energyOptions = [
  { 
    value: "supercharged", 
    label: "Supercharged! 💪", 
    description: "I'm feeling stronger than usual today!",
    avatar: "😎",
    color: "emerald-500",
    message: "Awesome! Let's increase the intensity today!"
  },
  { 
    value: "ready", 
    label: "Ready to crush it! 🔥", 
    description: "I'm feeling good and ready to go!",
    avatar: "🔥",
    color: "emerald-400",
    message: "Great! Let's stick to your regular plan."
  },
  { 
    value: "tired", 
    label: "A bit tired... 😴", 
    description: "I'm more tired than normal but can still workout.",
    avatar: "😴",
    color: "orange-400",
    message: "No problem! We'll adjust the intensity slightly."
  },
  { 
    value: "low", 
    label: "Tank's on empty ⚠️", 
    description: "I'm feeling weak or sore today.",
    avatar: "⚠️",
    color: "red-400",
    message: "Let's focus on recovery and light movement today."
  },
  { 
    value: "injured", 
    label: "Dealing with an injury ⚕️", 
    description: "I have a specific issue or injury to work around.",
    avatar: "⚕️",
    color: "purple-400",
    message: "Thanks for letting us know. We'll modify your workout accordingly."
  },
];

export function ActiveWorkoutIndicator() {
  const { isWorkoutActive, activeWorkout, returnToWorkout, endWorkout } = useWorkout();
  const [energyLevel, setEnergyLevel] = useState("ready"); // Default energy level
  const [showEnergyDialog, setShowEnergyDialog] = useState(false);
  const [animateEnergy, setAnimateEnergy] = useState(false);
  
  if (!isWorkoutActive) return null;
  
  const selectedOption = energyOptions.find(option => option.value === energyLevel);
  
  const handleEnergySelect = (value: string) => {
    setAnimateEnergy(true);
    setEnergyLevel(value);
    setShowEnergyDialog(false);
    
    // Reset animation after it completes
    setTimeout(() => setAnimateEnergy(false), 600);
  };
  
  const openEnergyDialog = () => {
    setShowEnergyDialog(true);
  };
  
  return (
    <>
      <div className="fixed bottom-[calc(var(--client-nav-total)+0.5rem)] left-0 right-0 z-50 flex justify-center px-4 pointer-events-none">
        <div 
          className={cn(
            "bg-gradient-to-r from-orange-500 to-orange-600 rounded-full py-2 px-4",
            "shadow-lg flex items-center justify-between w-full max-w-md pointer-events-auto"
          )}
        >
          <div className="flex min-w-0 items-center">
            <button 
              onClick={openEnergyDialog}
              className="bg-orange-100 rounded-full p-1 mr-3 flex items-center justify-center relative hover:bg-orange-200 transition-colors"
            >
              <span className={cn(
                "text-lg transition-transform", 
                animateEnergy ? "scale-125" : ""
              )}>
                {selectedOption?.avatar || "🏋️"}
              </span>
              <ChevronDown className="h-3 w-3 text-orange-600 absolute -bottom-1 -right-1 bg-orange-100 rounded-full p-0.5" />
            </button>
            <div className="min-w-0 pr-2 text-white">
              <p className="truncate text-sm font-medium">{activeWorkout?.title}</p>
              <p className="text-xs text-orange-100">Workout in progress</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => {
              console.log("End workout button clicked in ActiveWorkoutIndicator");
              endWorkout();
            }}
            aria-label="End workout"
            title="End workout"
            className="h-9 w-9 shrink-0 rounded-full bg-white/20 text-white hover:bg-white/30"
          >
            <Square className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Energy level selection dialog */}
      <Dialog open={showEnergyDialog} onOpenChange={setShowEnergyDialog}>
        <DialogContent className="max-w-md rounded-2xl p-0 overflow-hidden">
          <div className="bg-gradient-to-r from-orange-400 to-orange-600 p-6 text-white">
            <DialogHeader>
              <DialogTitle className="text-2xl text-center text-white">How are you feeling now?</DialogTitle>
            </DialogHeader>
            <div className="text-center mb-4 text-orange-100">
              We'll adjust your workout based on your energy level
            </div>
          </div>
          
          <div className="p-4">
            <RadioGroup 
              value={energyLevel} 
              onValueChange={handleEnergySelect} 
              className="gap-3"
            >
              {energyOptions.map((option) => (
                <label
                  key={option.value}
                  className={cn(
                    "flex items-center space-x-3 border border-gray-200 rounded-xl p-4 cursor-pointer transition-all",
                    energyLevel === option.value ? `border-${option.color} bg-${option.color}/10` : "",
                    "hover:border-orange-300 hover:bg-orange-50"
                  )}
                >
                  <RadioGroupItem value={option.value} id={option.value} className="text-orange-500" />
                  <div className="flex items-center flex-1">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mr-3 bg-orange-100 text-orange-600">
                      <span className="text-xl">{option.avatar}</span>
                    </div>
                    <div>
                      <span className="font-medium text-lg">{option.label}</span>
                      <span className="text-gray-600 text-sm block">{option.description}</span>
                    </div>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
